/**
 * Tech Tickets: staff raise requests for the tech team; super admins move
 * them through Request → Accepted → Working → RFI / Completed, or to
 * Backburner / Rejected at any point.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentUser } from "@/lib/session";
import { ROLE_LABEL, isStaffRole } from "@/lib/hub";
import { formatApiError } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  techTicketsApi as tt, type DeadlineKind, type TechTicket, type TechTicketDetail, type TicketAttachment, type TicketStatus,
} from "@/lib/techTicketsApi";

// ── Images (screenshots) ────────────────────────────────────────

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function imagesFrom(list: FileList | File[] | null | undefined): { ok: File[]; bad: string[] } {
  const ok: File[] = [], bad: string[] = [];
  for (const f of Array.from(list || [])) {
    if (!IMAGE_TYPES.includes(f.type)) bad.push(`${f.name || "That file"} isn't an image`);
    else if (f.size > MAX_IMAGE_BYTES) bad.push(`${f.name || "That image"} is over 10 MB`);
    else ok.push(f.name ? f : new File([f], `screenshot-${Date.now()}.png`, { type: f.type }));
  }
  return { ok, bad };
}

/** Pasted screenshots (Cmd/Ctrl+V) anywhere while `active`. */
function usePasteImages(active: boolean, onFiles: (files: File[]) => void) {
  const cb = useRef(onFiles);
  cb.current = onFiles;
  useEffect(() => {
    if (!active) return;
    const h = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
      if (files.length) { e.preventDefault(); cb.current(files); }
    };
    window.addEventListener("paste", h);
    return () => window.removeEventListener("paste", h);
  }, [active]);
}

/** "Add images" button + drop zone. Paste is handled by the modal. */
function ImagePicker({ onFiles, busy, hint }: { onFiles: (files: FileList | File[]) => void; busy?: boolean; hint?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
      className={cn("rounded-xl border border-dashed px-3 py-2.5 flex items-center gap-3 transition-colors",
        over ? "border-accent-500 bg-accent-500/10" : "border-surface-border")}>
      <button type="button" disabled={busy} onClick={() => ref.current?.click()}
        className="h-8 px-3 rounded-lg border border-surface-border text-caption font-medium text-dark-600 hover:bg-surface-secondary disabled:opacity-40">
        {busy ? "Uploading…" : "Add images"}
      </button>
      <span className="text-2xs text-dark-400">{hint || "or drop them here, or paste a screenshot (Ctrl/Cmd+V)"}</span>
      <input ref={ref} type="file" accept={IMAGE_TYPES.join(",")} multiple className="hidden"
        onChange={(e) => { onFiles(e.target.files || []); e.target.value = ""; }} />
    </div>
  );
}

function Thumb({ src, name, onRemove, href }: { src: string | null; name: string; onRemove?: () => void; href?: string | null }) {
  return (
    <div className="relative group w-28 h-20 rounded-lg overflow-hidden border border-surface-border bg-surface-secondary">
      {src ? (
        <a href={href || src} target="_blank" rel="noreferrer" title={name}>
          <img src={src} alt={name} className="w-full h-full object-cover" />
        </a>
      ) : <span className="absolute inset-0 grid place-items-center text-2xs text-dark-300 px-2 text-center">{name}</span>}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={`Remove ${name}`}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs leading-none opacity-0 group-hover:opacity-100 focus:opacity-100">×</button>
      )}
    </div>
  );
}

/** Local previews for files not uploaded yet. */
function usePreviews(files: File[]) {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  return urls;
}

const COLUMNS: { key: TicketStatus; label: string; hint: string }[] = [
  { key: "request", label: "Request", hint: "New, not yet picked up" },
  { key: "accepted", label: "Accepted", hint: "Confirmed, not started" },
  { key: "working", label: "Working", hint: "In progress" },
  { key: "rfi", label: "RFI", hint: "Waiting on information" },
  { key: "completed", label: "Completed", hint: "Done" },
  { key: "backburner", label: "Backburner", hint: "Parked for now" },
  { key: "rejected", label: "Rejected", hint: "Won't be done" },
];
const NEXT: Partial<Record<TicketStatus, { to: TicketStatus; label: string }>> = {
  request: { to: "accepted", label: "Accept" },
  accepted: { to: "working", label: "Start" },
  working: { to: "completed", label: "Complete" },
  rfi: { to: "working", label: "Resume" },
};
const SOFTWARE: Record<string, string> = {
  excel: "Excel", mfx_platform: "MFX Platform", training_platform: "Training Platform",
  recruitment_platform: "Recruitment Platform", internal_tool: "Internal tool",
};

export function departmentLabel(role: string | null | undefined): string {
  if (isStaffRole(role)) return ROLE_LABEL[role];
  if (role === "super_admin") return "Admin";
  return role || "Staff";
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
const ago = (iso: string) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
};
function daysUntil(iso: string): number {
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - t.getTime()) / 86400000);
}

function Pill({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn("inline-flex items-center text-2xs font-medium rounded-full px-2 py-0.5 whitespace-nowrap", className)}>{children}</span>;
}

function DeadlinePill({ t }: { t: TechTicket }) {
  if (t.deadlineKind === "none" || !t.deadline) return null;
  const d = daysUntil(t.deadline);
  const open = !["completed", "rejected"].includes(t.status);
  const urgent = open && t.deadlineKind === "hard" && d <= 3;
  return (
    <Pill className={urgent ? "bg-status-error-bg text-status-error"
      : t.deadlineKind === "hard" ? "bg-status-warning-bg text-status-warning" : "bg-surface-secondary text-dark-500"}>
      {t.deadlineKind === "hard" ? "Hard" : "Soft"} {new Date(t.deadline).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
      {open && d < 0 ? " · overdue" : ""}
    </Pill>
  );
}

function TypePill({ type }: { type: TechTicket["requestType"] }) {
  return type === "bug"
    ? <Pill className="bg-status-error-bg text-status-error">Bug</Pill>
    : <Pill className="bg-brand-500/15 text-brand-600">Feature</Pill>;
}

function CreatorPill({ t }: { t: TechTicket }) {
  // Truncates inside the card rather than spilling over its edge.
  const label = `${t.createdByName} · ${departmentLabel(t.createdByRole)}`;
  return (
    <span title={label} className="inline-block max-w-full min-w-0 truncate text-2xs font-medium rounded-full px-2 py-0.5 bg-surface-secondary text-dark-600">
      {label}
    </span>
  );
}

// ── Card ────────────────────────────────────────────────────────

function TicketCard({ t, canManage, onOpen, onMove }: {
  t: TechTicket; canManage: boolean; onOpen: () => void; onMove: (to: TicketStatus) => void;
}) {
  const next = NEXT[t.status];
  return (
    <div onClick={onOpen}
      className="min-w-0 overflow-hidden rounded-xl border border-surface-border bg-white p-3 cursor-pointer hover:border-brand-500/50 transition-colors">
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        <span className="text-2xs text-dark-300 font-mono">#{t.number}</span>
        <TypePill type={t.requestType} />
        <Pill className="bg-surface-secondary text-dark-500">{t.softwareLabel}</Pill>
        <DeadlinePill t={t} />
      </div>
      <p className="text-body-sm text-dark-800 line-clamp-3 whitespace-pre-line">{t.description}</p>
      {t.status === "backburner" && (
        <p className="text-2xs text-dark-400 mt-1.5">
          {t.backburnerUntil ? `Back ~${fmtDate(t.backburnerUntil)} · ` : ""}{t.statusReason}
        </p>
      )}
      {t.status === "rejected" && t.statusReason && <p className="text-2xs text-dark-400 mt-1.5">{t.statusReason}</p>}
      {t.status === "completed" && t.statusChangedAt && (
        <p className="text-2xs text-dark-400 mt-1.5">Completed {fmtDate(t.statusChangedAt)}{t.statusChangedBy ? ` by ${t.statusChangedBy}` : ""}</p>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-2.5 min-w-0">
        <CreatorPill t={t} />
        <div className="flex items-center gap-2 text-2xs text-dark-300 ml-auto whitespace-nowrap shrink-0">
          {!!t.attachmentCount && <span title="Images">📎 {t.attachmentCount}</span>}
          {t.commentCount > 0 && <span title="Comments">💬 {t.commentCount}</span>}
          <span title={new Date(t.createdAt).toLocaleString("en-GB")}>{ago(t.createdAt)}</span>
          {canManage && next && (
            <button onClick={(e) => { e.stopPropagation(); onMove(next.to); }}
              className="h-6 px-2 rounded-md bg-brand-500 text-white text-2xs font-medium hover:bg-brand-600">
              {next.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── New ticket ──────────────────────────────────────────────────

function NewTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [software, setSoftware] = useState("");
  const [type, setType] = useState<"" | "feature" | "bug">("");
  const [kind, setKind] = useState<DeadlineKind>("none");
  const [deadline, setDeadline] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [imageErr, setImageErr] = useState("");
  const previews = usePreviews(images);
  const addImages = (list: FileList | File[]) => {
    const { ok, bad } = imagesFrom(list);
    setImageErr(bad.join("; "));
    setImages((cur) => [...cur, ...ok].slice(0, 20));
  };
  usePasteImages(true, addImages);
  const create = useMutation({
    mutationFn: async () => {
      const made = await tt.create({ software, requestType: type, deadlineKind: kind, deadline: kind === "none" ? undefined : deadline, description });
      const failed: string[] = [];
      for (const f of images) {
        try { await tt.attach(made.id, f); } catch (e) { failed.push(`${f.name}: ${formatApiError(e, "didn't upload")}`); }
      }
      return failed;
    },
    onSuccess: (failed) => {
      onCreated();
      if (failed.length) { setImageErr(`Ticket created, but some images didn't upload (${failed.join("; ")}). Open the ticket to add them.`); return; }
      onClose();
    },
  });
  const ready = software && type && description.trim().length >= 5 && (kind === "none" || deadline);
  const choice = (on: boolean) => cn("h-9 px-3 rounded-lg text-body-sm border-[1.5px] transition-colors",
    on ? "bg-accent-500/15 text-dark-800 font-medium border-black dark:border-white"
       : "bg-white border-surface-border text-dark-600 hover:border-dark-400");
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-6 my-10" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-heading-md text-dark-800 mb-4">New tech ticket</h2>

        <label className="block text-caption font-medium text-dark-600 mb-1.5">1. Which software?</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(SOFTWARE).map(([k, v]) => (
            <button key={k} type="button" onClick={() => setSoftware(k)} className={choice(software === k)}>{v}</button>
          ))}
        </div>

        <label className="block text-caption font-medium text-dark-600 mb-1.5">2. What kind of request?</label>
        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => setType("feature")} className={choice(type === "feature")}>New feature</button>
          <button type="button" onClick={() => setType("bug")} className={choice(type === "bug")}>Bug fix</button>
        </div>

        <label className="block text-caption font-medium text-dark-600 mb-1.5">3. Is there a deadline?</label>
        <div className="flex flex-wrap gap-2 mb-1.5">
          <button type="button" onClick={() => setKind("none")} className={choice(kind === "none")}>No deadline</button>
          <button type="button" onClick={() => setKind("soft")} className={choice(kind === "soft")}>Soft: we'd love it by…</button>
          <button type="button" onClick={() => setKind("hard")} className={choice(kind === "hard")}>Hard: it must happen by…</button>
        </div>
        {kind !== "none" && (
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
            className="h-9 rounded-lg border border-surface-border px-3 text-body-sm mb-4" />
        )}
        {kind === "none" && <div className="mb-4" />}

        <label className="block text-caption font-medium text-dark-600 mb-1.5">4. Describe the request</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={7}
          placeholder="What do you need, and why? For a bug: what you did, what happened, and what you expected."
          className="w-full rounded-xl border border-surface-border px-3 py-2 text-body-sm resize-y" />

        <label className="block text-caption font-medium text-dark-600 mt-4 mb-1.5">5. Screenshots (optional)</label>
        <ImagePicker onFiles={addImages} busy={create.isPending} />
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {images.map((f, i) => (
              <Thumb key={previews[i]} src={previews[i]} name={f.name}
                onRemove={create.isPending ? undefined : () => setImages((cur) => cur.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}
        {imageErr && <p className="text-caption text-status-error mt-2">{imageErr}</p>}

        {create.isError && <p className="text-body-sm text-status-error mt-2">{formatApiError(create.error, "Couldn't create the ticket")}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="h-9 px-4 rounded-xl text-body-sm text-dark-600 hover:bg-surface-secondary">Cancel</button>
          <button onClick={() => create.mutate()} disabled={!ready || create.isPending}
            className="h-9 px-4 rounded-xl text-body-sm font-medium bg-brand-500 text-white disabled:opacity-40">
            {create.isPending ? "Creating…" : "Create ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ticket detail ───────────────────────────────────────────────

function TicketModal({ id, canManage, onClose, onChanged }: {
  id: string; canManage: boolean; onClose: () => void; onChanged: () => void;
}) {
  const q = useQuery({ queryKey: ["tech-ticket", id], queryFn: () => tt.get(id) });
  const qc = useQueryClient();
  const [comment, setComment] = useState("");
  const [panel, setPanel] = useState<"" | "backburner" | "rejected" | "delete">("");
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const done = (data?: unknown) => {
    if (data) qc.setQueryData(["tech-ticket", id], data);
    onChanged();
  };
  const move = useMutation({
    mutationFn: (v: { to: TicketStatus; reason?: string; until?: string }) => tt.setStatus(id, v.to, v.reason, v.until),
    onSuccess: (d) => { setPanel(""); setReason(""); setUntil(""); done(d); },
  });
  const post = useMutation({ mutationFn: () => tt.comment(id, comment), onSuccess: (d) => { setComment(""); done(d); } });
  const del = useMutation({ mutationFn: () => tt.remove(id), onSuccess: () => { onChanged(); onClose(); } });
  const [uploading, setUploading] = useState(false);
  const [imageErr, setImageErr] = useState("");
  const upload = async (list: FileList | File[]) => {
    const { ok, bad } = imagesFrom(list);
    const errs = [...bad];
    if (!ok.length) { setImageErr(errs.join("; ")); return; }
    setUploading(true);
    let last: TechTicketDetail | null = null;
    for (const f of ok) {
      try { last = await tt.attach(id, f); } catch (e) { errs.push(`${f.name}: ${formatApiError(e, "didn't upload")}`); }
    }
    setUploading(false);
    setImageErr(errs.join("; "));
    if (last) done(last);
  };
  usePasteImages(!uploading, upload);
  const detach = useMutation({ mutationFn: (a: TicketAttachment) => tt.detach(id, a.id), onSuccess: (d) => done(d) });
  const me = getCurrentUser();
  const t = q.data;
  const err = move.error || post.error || del.error || detach.error;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        {!t ? <div className="h-40 rounded-xl bg-surface-secondary animate-pulse" /> : (
          <>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-heading-sm text-dark-800 font-mono">#{t.number}</span>
                <TypePill type={t.requestType} />
                <Pill className="bg-surface-secondary text-dark-500">{t.softwareLabel}</Pill>
                <DeadlinePill t={t} />
                <Pill className="bg-brand-500/15 text-brand-600">{COLUMNS.find((c) => c.key === t.status)?.label}</Pill>
              </div>
              <button onClick={onClose} className="text-dark-400 hover:text-dark-700 text-xl leading-none">×</button>
            </div>
            <p className="text-caption text-dark-400 mb-3">
              Raised by <CreatorPill t={t} /> on {new Date(t.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              {t.deadline ? ` · ${t.deadlineKind === "hard" ? "must happen by" : "would love it by"} ${fmtDate(t.deadline)}` : ""}
            </p>
            <div className="rounded-xl bg-surface-secondary/60 p-4 text-body-sm text-dark-800 whitespace-pre-wrap [overflow-wrap:anywhere]">{t.description}</div>
            <div className="mt-3">
              {(t.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {t.attachments.map((a) => (
                    <Thumb key={a.id} src={a.url} name={a.filename || "image"}
                      onRemove={canManage || String(a.addedById ?? "") === String(me?.id ?? "") ? () => detach.mutate(a) : undefined} />
                  ))}
                </div>
              )}
              <ImagePicker onFiles={upload} busy={uploading} />
              {imageErr && <p className="text-caption text-status-error mt-1.5">{imageErr}</p>}
            </div>
            {(t.status === "backburner" || t.status === "rejected") && (t.statusReason || t.backburnerUntil) && (
              <p className="text-body-sm text-dark-600 mt-2">
                {t.status === "backburner" ? "On the backburner" : "Rejected"}
                {t.backburnerUntil ? ` until about ${fmtDate(t.backburnerUntil)}` : ""}{t.statusReason ? `: ${t.statusReason}` : ""}
              </p>
            )}

            {canManage && (
              <div className="mt-4 rounded-xl border border-surface-border p-3">
                <p className="text-caption font-medium text-dark-600 mb-2">Move to</p>
                <div className="flex flex-wrap gap-1.5">
                  {COLUMNS.filter((c) => c.key !== t.status).map((c) => (
                    <button key={c.key} disabled={move.isPending}
                      onClick={() => (c.key === "backburner" || c.key === "rejected") ? setPanel(c.key) : move.mutate({ to: c.key })}
                      className={cn("h-8 px-3 rounded-lg text-caption font-medium border",
                        c.key === "rejected" ? "border-status-error/40 text-status-error"
                          : c.key === "backburner" ? "border-surface-border text-dark-500" : "border-brand-500/40 text-brand-600",
                        "hover:bg-surface-secondary disabled:opacity-40")}>
                      {c.label}
                    </button>
                  ))}
                  <button onClick={() => setPanel("delete")}
                    className="h-8 px-3 rounded-lg text-caption font-medium text-dark-400 hover:text-status-error ml-auto">
                    Delete
                  </button>
                </div>
                {panel === "backburner" && (
                  <div className="mt-3 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
                    <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)"
                      className="h-9 rounded-lg border border-surface-border px-3 text-body-sm" />
                    <label className="text-2xs text-dark-400">Back in rotation around (optional)
                      <input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
                        className="block h-9 rounded-lg border border-surface-border px-3 text-body-sm mt-0.5" />
                    </label>
                    <button disabled={!reason.trim() || move.isPending} onClick={() => move.mutate({ to: "backburner", reason, until: until || undefined })}
                      className="h-9 px-3 rounded-lg bg-brand-500 text-white text-caption font-medium disabled:opacity-40">Backburner it</button>
                  </div>
                )}
                {panel === "rejected" && (
                  <div className="mt-3 flex gap-2">
                    <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
                      className="flex-1 h-9 rounded-lg border border-surface-border px-3 text-body-sm" />
                    <button disabled={move.isPending} onClick={() => move.mutate({ to: "rejected", reason: reason || undefined })}
                      className="h-9 px-3 rounded-lg bg-status-error text-white text-caption font-medium disabled:opacity-40">Reject</button>
                  </div>
                )}
                {panel === "delete" && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-body-sm text-dark-600 flex-1">Delete this ticket? Only for tickets made in error.</span>
                    <button onClick={() => setPanel("")} className="h-9 px-3 rounded-lg text-caption text-dark-500">Cancel</button>
                    <button disabled={del.isPending} onClick={() => del.mutate()}
                      className="h-9 px-3 rounded-lg bg-status-error text-white text-caption font-medium disabled:opacity-40">Delete ticket</button>
                  </div>
                )}
              </div>
            )}

            <h3 className="text-caption font-medium text-dark-600 mt-5 mb-2">Comments</h3>
            <ul className="space-y-2">
              {t.comments.map((c) => c.isSystem ? (
                <li key={c.id} className="text-2xs text-dark-400">
                  {c.authorName}: {c.body} · {new Date(c.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </li>
              ) : (
                <li key={c.id} className="rounded-xl border border-surface-border p-3">
                  <p className="text-2xs text-dark-400 mb-1">
                    <span className="font-medium text-dark-700">{c.authorName}</span> · {departmentLabel(c.authorRole)} ·{" "}
                    {new Date(c.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="text-body-sm text-dark-800 whitespace-pre-wrap [overflow-wrap:anywhere]">{c.body}</p>
                </li>
              ))}
              {t.comments.length === 0 && <li className="text-body-sm text-dark-300">No comments yet.</li>}
            </ul>
            <div className="mt-3 flex gap-2 items-end">
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Add a comment…"
                className="flex-1 rounded-xl border border-surface-border px-3 py-2 text-body-sm resize-y" />
              <button disabled={!comment.trim() || post.isPending} onClick={() => post.mutate()}
                className="h-9 px-4 rounded-xl bg-brand-500 text-white text-body-sm font-medium disabled:opacity-40">Post</button>
            </div>
            {err && <p className="text-body-sm text-status-error mt-2">{formatApiError(err, "That didn't work")}</p>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────

export default function TechTicketsPage() {
  const user = getCurrentUser();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tech-tickets"], queryFn: tt.list, refetchInterval: 60_000 });
  const [creating, setCreating] = useState(false);
  // /tech-tickets?ticket=<id> (the link in Teams) opens that ticket.
  const [openId, setOpenId] = useState<string | null>(() => new URLSearchParams(window.location.search).get("ticket"));
  const [dept, setDept] = useState("all");
  const [software, setSoftware] = useState("all");
  const [type, setType] = useState("all");
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"board" | "completed">("board");
  // Parked and rejected tickets are hidden on the board unless asked for.
  const [showBackburner, setShowBackburner] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["tech-tickets"] });
  const move = useMutation({
    mutationFn: (v: { id: string; to: TicketStatus }) => tt.setStatus(v.id, v.to),
    onSuccess: refresh,
  });
  const canManage = !!q.data?.canManage;
  const tickets = q.data?.tickets || [];

  const depts = useMemo(() => {
    const m = new Map<string, number>();
    tickets.forEach((t) => { const d = departmentLabel(t.createdByRole); m.set(d, (m.get(d) || 0) + 1); });
    return Array.from(m.entries()).sort();
  }, [tickets]);
  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return tickets.filter((t) =>
      (dept === "all" || departmentLabel(t.createdByRole) === dept) &&
      (software === "all" || t.software === software) &&
      (type === "all" || t.requestType === type) &&
      (!mine || t.createdById === String(user?.id ?? "")) &&
      (!s || `#${t.number} ${t.description} ${t.createdByName} ${t.softwareLabel}`.toLowerCase().includes(s)));
  }, [tickets, dept, software, type, mine, search, user]);

  const count = (st: TicketStatus) => visible.filter((t) => t.status === st).length;
  const columns = COLUMNS.filter((c) => c.key !== "completed"
    && (c.key !== "backburner" || showBackburner) && (c.key !== "rejected" || showRejected));
  const completed = visible.filter((t) => t.status === "completed")
    .sort((a, b) => (b.statusChangedAt || "").localeCompare(a.statusChangedAt || ""));
  const openCount = visible.filter((t) => !["completed", "backburner", "rejected"].includes(t.status)).length;

  const select = "h-9 rounded-lg border border-surface-border bg-white px-2.5 text-body-sm text-dark-700";
  const toggle = (on: boolean) => cn("h-9 px-3 rounded-lg text-body-sm font-medium border transition-colors",
    on ? "bg-accent-500/10 border-accent-500 text-dark-800" : "bg-white border-surface-border text-dark-500 hover:bg-surface-secondary");
  const tabCls = (on: boolean) => cn("h-9 px-4 text-body-sm font-medium transition-colors",
    on ? "bg-accent-500/10 text-dark-800" : "bg-white text-dark-500 hover:bg-surface-secondary");

  const card = (t: TechTicket) => (
    <TicketCard key={t.id} t={t} canManage={canManage}
      onOpen={() => setOpenId(t.id)} onMove={(to) => move.mutate({ id: t.id, to })} />
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-heading-lg text-dark-800">Tech Tickets</h1>
          <p className="text-body-sm text-dark-400 mt-0.5">Ask the tech team for a new feature or a fix, and follow it through.</p>
        </div>
        <button onClick={() => setCreating(true)}
          className="h-9 px-4 rounded-xl text-body-sm font-medium bg-brand-500 text-white hover:bg-brand-600">New ticket</button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-surface-border overflow-hidden divide-x divide-surface-border">
          <button type="button" className={tabCls(tab === "board")} onClick={() => setTab("board")}>
            Open <span className="text-dark-400 font-normal">{openCount}</span>
          </button>
          <button type="button" className={tabCls(tab === "completed")} onClick={() => setTab("completed")}>
            Completed <span className="text-dark-400 font-normal">{completed.length}</span>
          </button>
        </div>
        <select value={dept} onChange={(e) => setDept(e.target.value)} className={select} aria-label="Department">
          <option value="all">All departments ({tickets.length})</option>
          {depts.map(([d, n]) => <option key={d} value={d}>{d} ({n})</option>)}
        </select>
        <select value={software} onChange={(e) => setSoftware(e.target.value)} className={select} aria-label="Software">
          <option value="all">All software</option>
          {Object.entries(SOFTWARE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className={select} aria-label="Type">
          <option value="all">Features and bugs</option>
          <option value="feature">Features</option>
          <option value="bug">Bugs</option>
        </select>
        <button type="button" onClick={() => setMine(!mine)} className={toggle(mine)}>Mine</button>
        {tab === "board" && (
          <>
            <button type="button" onClick={() => setShowBackburner(!showBackburner)} className={toggle(showBackburner)}
              aria-pressed={showBackburner}>
              {showBackburner ? "Hide" : "Show"} backburner <span className="opacity-70">{count("backburner")}</span>
            </button>
            <button type="button" onClick={() => setShowRejected(!showRejected)} className={toggle(showRejected)}
              aria-pressed={showRejected}>
              {showRejected ? "Hide" : "Show"} rejected <span className="opacity-70">{count("rejected")}</span>
            </button>
          </>
        )}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
          className="ml-auto h-9 w-60 max-w-full rounded-lg border border-surface-border bg-white px-3 text-body-sm" />
      </div>

      {q.isError && <p className="text-body-sm text-status-error">{formatApiError(q.error, "Couldn't load tickets")}</p>}
      {move.isError && <p className="text-body-sm text-status-error">{formatApiError(move.error, "Couldn't move the ticket")}</p>}

      {tab === "board" ? (
        <div className="overflow-x-auto pb-2">
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(210px, 1fr))` }}>
            {columns.map((col) => {
              const items = visible.filter((t) => t.status === col.key)
                .sort((a, b) => col.key === "rejected"
                  ? (b.statusChangedAt || "").localeCompare(a.statusChangedAt || "")
                  : (a.createdAt).localeCompare(b.createdAt));
              return (
                <div key={col.key} className="min-w-0 rounded-2xl bg-surface-secondary/50 border border-surface-border p-2.5 min-h-[200px]">
                  <div className="flex items-baseline justify-between px-1 mb-2" title={col.hint}>
                    <h2 className="text-body-sm font-semibold text-dark-700">{col.label}</h2>
                    <span className="text-2xs text-dark-400">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map(card)}
                    {items.length === 0 && <p className="text-2xs text-dark-300 px-1">Nothing here.</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        completed.length === 0 ? (
          <p className="text-body-sm text-dark-400 py-8 text-center">No completed tickets{visible.length !== tickets.length ? " match the filters" : " yet"}.</p>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
            {completed.map(card)}
          </div>
        )
      )}

      {creating && <NewTicketModal onClose={() => setCreating(false)} onCreated={refresh} />}
      {openId && <TicketModal id={openId} canManage={canManage} onChanged={refresh} onClose={() => {
        setOpenId(null);
        if (window.location.search.includes("ticket=")) window.history.replaceState(null, "", window.location.pathname);
      }} />}
    </div>
  );
}
